import { useEffect, useRef, useState } from 'react';

/**
 * SplitFlap — airport split-flap (Solari) display, in-house.
 *
 * Renders a row of dark flap cells with off-white characters and a hairline
 * seam. On a value change each cell drum-clatters to its new glyph. Used only
 * for the Flight Deck signature elements (board title, clock, portfolio
 * numbers, card name / headline KPI / countdown) per the redesign spec §11.
 *
 * Honours `prefers-reduced-motion`: with motion reduced, cells show the final
 * value instantly and the flap cells are aria-hidden behind a plain-text label.
 */

const ALPHANUM = ' ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789.,:%+-/&$ ';
const NUMERIC = ' 0123456789.,:%+-';

const SIZES: Record<string, { w: number; h: number; fs: number }> = {
  sm: { w: 15, h: 22, fs: 12 },
  md: { w: 22, h: 32, fs: 18 },
  lg: { w: 31, h: 45, fs: 27 },
  hero: { w: 40, h: 58, fs: 37 },
};

export interface SplitFlapProps {
  /** Target text, already formatted. */
  value: string;
  /** Fixed cell count; pads with blanks. Defaults to value length. */
  chars?: number;
  size?: 'sm' | 'md' | 'lg' | 'hero';
  charSet?: 'alphanumeric' | 'numeric';
  /** Per-flap duration (ms). */
  flipMs?: number;
  /** Left-to-right per-cell start offset (ms). */
  staggerMs?: number;
  /** true = drum-clatter through glyphs; false = a single flip. */
  cascade?: boolean;
  /** true = the loading shimmer (cells rest on blanks). */
  idle?: boolean;
  /** Render characters in the amber alert colour. */
  accent?: boolean;
  /** Called once after the last cell settles. */
  onSettled?: () => void;
  className?: string;
  'aria-label'?: string;
}

/** Shared reduced-motion hook — also used by the card flip + entrance. */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

/** The drum sequence ending on `to`: a bounded clatter, target last. */
function drum(to: string, set: string, cascade: boolean): string[] {
  if (!cascade) return [to];
  const ti = Math.max(0, set.indexOf(to));
  const N = 11;
  const seq: string[] = [];
  for (let k = N; k >= 0; k--) seq.push(set[(ti - k + set.length * 4) % set.length] ?? to);
  return seq;
}

export function SplitFlap({
  value,
  chars,
  size = 'md',
  charSet = 'alphanumeric',
  flipMs = 130,
  staggerMs = 40,
  cascade = true,
  idle = false,
  accent = false,
  onSettled,
  className,
  'aria-label': ariaLabel,
}: SplitFlapProps) {
  const reduced = usePrefersReducedMotion();
  const set = charSet === 'numeric' ? NUMERIC : ALPHANUM;
  const dims = SIZES[size] ?? SIZES.md!;

  const raw = (value ?? '').toUpperCase();
  const count = chars ?? (raw.length || 1);
  // numeric values right-align (pad start); text left-aligns (pad end).
  const padded =
    charSet === 'numeric'
      ? raw.slice(-count).padStart(count, ' ')
      : raw.slice(0, count).padEnd(count, ' ');
  const cellChars = padded.split('');

  return (
    <span
      className={`splitflap-row${className ? ' ' + className : ''}`}
      role="img"
      aria-label={ariaLabel ?? value}
    >
      {cellChars.map((ch, i) => (
        <FlapCell
          key={i}
          target={set.includes(ch) ? ch : ' '}
          dims={dims}
          set={set}
          flipMs={flipMs}
          startDelay={i * staggerMs}
          cascade={cascade}
          idle={idle}
          accent={accent}
          reduced={reduced}
          onSettled={i === cellChars.length - 1 ? onSettled : undefined}
        />
      ))}
    </span>
  );
}

interface FlapCellProps {
  target: string;
  dims: { w: number; h: number; fs: number };
  set: string;
  flipMs: number;
  startDelay: number;
  cascade: boolean;
  idle: boolean;
  accent: boolean;
  reduced: boolean;
  onSettled?: (() => void) | undefined;
}

function FlapCell({
  target,
  dims,
  set,
  flipMs,
  startDelay,
  cascade,
  idle,
  accent,
  reduced,
  onSettled,
}: FlapCellProps) {
  const [display, setDisplay] = useState<string>(reduced ? target : ' ');
  const timers = useRef<number[]>([]);

  useEffect(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    if (idle) {
      setDisplay(' ');
      return;
    }
    if (reduced) {
      setDisplay(target);
      onSettled?.();
      return;
    }
    let i = 0;
    const seq = drum(target, set, cascade);
    let cancelled = false;
    const step = () => {
      if (cancelled) return;
      setDisplay(seq[i] ?? target);
      i += 1;
      if (i < seq.length) {
        timers.current.push(window.setTimeout(step, flipMs));
      } else {
        onSettled?.();
      }
    };
    timers.current.push(window.setTimeout(step, startDelay));
    return () => {
      cancelled = true;
      timers.current.forEach(clearTimeout);
      timers.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, idle, reduced]);

  return (
    <span
      className={`flap-cell${accent ? ' accent' : ''}${idle ? ' idle' : ''}`}
      style={{ width: dims.w, height: dims.h, fontSize: dims.fs }}
      aria-hidden="true"
    >
      <span
        key={display}
        className={reduced || idle ? 'flap-char' : 'flap-char anim'}
        style={{ animationDuration: `${flipMs}ms` }}
      >
        {display === ' ' ? ' ' : display}
      </span>
      <span className="flap-seam" />
    </span>
  );
}
